/* packages/plan-catalog/index.js */
/* eslint max-len: ["error", { "code": 80 }] */

/**
 * CENTRAL PLAN CATALOG — SOURCE OF TRUTH
 *
 * Frontend:
 *   import { planConfigs, priceIdToPlanKey } from "plan-catalog";
 *
 * Cloud Functions (CJS):
 *   const {
 *     planConfigs,
 *     priceIdToPlanKey,
 *     planKeyToEntitlements,
 *     priceIdToEntitlements,
 *   } = require("plan-catalog");
 *
 * --------------------------------------------------------------------
 * LAUNCH VISIBILITY / ADD-ONS
 * --------------------------------------------------------------------
 * You asked to hide add-ons in the UI until the Stripe side is ready.
 *
 * IMPORTANT: We do NOT remove add-ons from this catalog, and we do NOT
 * change any pricing IDs, entitlement mappings, or lookup tables.
 *
 * Instead, we add a small, non-breaking metadata field:
 *   • kind: "base" | "addon"
 *
 * This lets the UI filter by cfg.kind === "base" without hard-coding
 * plan keys. Existing subscription and seat-change logic will NOT break
 * because:
 *   1) priceIdToPlanKey is still generated from ALL entries (base+addon)
 *   2) planKeyToEntitlements is still generated from ALL entries
 *   3) All existing keys (standard/pro/enterprise/...) remain unchanged
 *   4) Stripe webhooks & sync continue mapping by priceId exactly as today
 *
 * The UI change is simply: only display plans where cfg.kind === "base".
 *
 * --------------------------------------------------------------------
 * ENTERPRISE SAAS BEST PRACTICE: CATALOG-DRIVEN CTA + SELF-SERVE POLICY
 * --------------------------------------------------------------------
 * You asked to avoid hard-coding "enterprise" logic in the UI and keep
 * ONE source of truth for plan behavior across:
 *   • Marketing pricing page (WordPress or React)
 *   • In-app subscription/billing page
 *   • Cloud Functions enforcement
 *
 * To support that, we add non-breaking metadata to each plan:
 *   • availability.selfServe: boolean
 *       - true  → plan can be purchased/changed via Stripe self-serve flows
 *       - false → plan must be handled via Sales / contract (no self-serve)
 *   • cta: machine-readable CTA config (label/type/contact)
 *       - UI can render "Start free trial" or "Contact Sales" consistently
 *       - UI can avoid showing prices when pricingDisplay === "contact"
 *   • constraints: optional seat limits for UI consistency
 *       - keeps seat selector / copy aligned with actual limits
 *
 * IMPORTANT:
 *  - We do NOT remove enterprise pricing tiers or change price IDs here,
 *    because existing lookups rely on those mappings. However, we mark
 *    enterprise as selfServe:false so the UI + server can block accidental
 *    Stripe calls if price IDs are archived/placeholder.
 *
 *  - Existing consumers that ignore these new fields will continue to work.
 *
 * --------------------------------------------------------------------
 * ENVIRONMENT-AWARE BILLING CATALOG
 * --------------------------------------------------------------------
 * This catalog now supports:
 *   • prod     → live Stripe price IDs
 *   • staging  → Stripe sandbox/test price IDs
 *
 * Resolution order:
 *   1) process.env.BILLING_ENV
 *   2) process.env.REACT_APP_BILLING_ENV
 *   3) fallback → "prod"
 *
 * IMPORTANT:
 *  - This defaults safely to production.
 *  - Production behavior remains unchanged unless a staging billing env
 *    is explicitly set.
 *  - Existing code can continue reading tier.priceId because we resolve
 *    the active environment into a backwards-compatible `priceId` field.
 */

/* ──────────────────────────────────────────────────────────────
 * Billing environment resolution
 * ─────────────────────────────────────────────────────────── */

const BILLING_ENV_PROD = "prod";
const BILLING_ENV_STAGING = "staging";

function normalizeBillingEnv(value) {
  const env = (value || "").toString().trim().toLowerCase();

  if (env === BILLING_ENV_STAGING) return BILLING_ENV_STAGING;
  return BILLING_ENV_PROD;
}

function resolveBillingEnv() {
  return normalizeBillingEnv(
      process.env.BILLING_ENV ||
      process.env.REACT_APP_BILLING_ENV ||
      BILLING_ENV_PROD,
  );
}

const ACTIVE_BILLING_ENV = resolveBillingEnv();

/* ──────────────────────────────────────────────────────────────
 * Billing policy
 * ───────────────────────────────────────────────────────────
 *
 * Enterprise rule:
 * - Trial duration is a backend-owned billing policy.
 * - The frontend may read this for fallback display/bootstrap only.
 * - The frontend must NOT be allowed to decide trial length by passing
 *   trialDays into a callable.
 */
const BILLING_POLICY = Object.freeze({
  selfServeTrialDays: 14,
  defaultTrialPlanKey: "standard",
  defaultTrialInterval: "monthly",
});


/* ──────────────────────────────────────────────────────────────
 * Presentation/content contract + merge helper exports
 * ─────────────────────────────────────────────────────────── */

const pricingPresentationContract = require("./pricingPresentationContract");
const {
  buildPricingPresentationPayload,
} = require("./buildPricingPresentationPayload");

/* ──────────────────────────────────────────────────────────────
 * Raw plan catalog with per-environment Stripe IDs
 * ─────────────────────────────────────────────────────────── */

const rawPlanConfigs = {
  /* ---------------------- STANDARD ---------------------- */
  standard: {
    kind: "base",

    /* ───────────────────────────────────────────────────────────────
     * Self-serve + CTA policy (NEW)
     * ----------------------------------------------------------------
     * Standard is self-serve. Public pricing shows "Start free trial";
     * in-app billing shows "Continue"/"Upgrade"/etc. based on state.
     * ---------------------------------------------------------------- */
    availability: {selfServe: true},
    cta: {
      type: "start_trial",
      label: "Start free trial",
    },

    /* Seat constraints
     *
     * UPDATED SEAT MODEL
     * ------------------
     * The new app pricing flow allows organizations to define their own
     * seat count directly in the UI instead of forcing the old
     * "starts at 10 seats / increments of 5" approach.
     *
     * Enterprise-safe rollout decision:
     * - minSeats: 1   → allows true typed seat selection from the new UI
     * - step: 1       → supports single-seat increments in the new UI
     * - maxSeats: 50  → keep existing self-serve upper bound for now
     *
     * IMPORTANT:
     * - We are intentionally changing the source-of-truth constraint layer
     *   here first so the UI and billing runtime can align to it.
     * - If you later want to expand or remove self-serve maximums, do that
     *   in a separate follow-up change after backend/runtime validation.
     */
    constraints: {
      minSeats: 1,
      maxSeats: 50,
      step: 1,
    },

    subheader: "Great for small-medium teams:",
    features: [
      {
        name: "Flexible user seats",
        tooltip: "Add from 1 user up to 50 users in self-serve.",
      },
      {
        name: "Unlimited UTM form types",
        tooltip:
          "Create any number of custom UTM-specific, single-page " +
          "for your projects.",
      },
      {
        name: "Unlimited clicks, links",
        tooltip: "No limits to links in published forms.",
      },
      {
        name: "Custom parameter rules",
        tooltip: "Limit capitalization, spacing, and more.",
      },
      {
        name: "Custom UTM groupings",
        tooltip: "Customize how UTMs are concatenated.",
      },
      {
        name: "Form Templates",
        tooltip:
          "App-wide or publish your own organization-wide form " +
          "templates.",
      },
      {
        name: "5 vanity form URLs",
        tooltip: "Customize up to 5 vanity URLs per plan.",
      },
      {
        name: "Premium dropdowns & dependencies",
        tooltip:
          "Manage dropdowns & dependencies in spreadsheet-like " +
          "views. Up to 25 options per dropdown.",
      },
      {
        name: "Form Submission Analytics",
        tooltip: "View form submissions from one place.",
      },
      {
        name: "User Roles",
        tooltip: "Define view only or editor permissions.",
      },
      {
        name: "Public & private sharing",
        tooltip:
          "Share forms publicly, organization-wide or with " +
          "specific users.",
      },
      {name: "Email Support"},
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 15,
        productIds: {
          prod: null,
          staging: "prod_U8SigNiVkQR42r",
        },
        lookupKeys: {
          prod: null,
          staging: "standard_monthly_perseat_flat",
        },
        priceIds: {
          prod: "price_1SxVkQ2KQrGGmiVJjGvLgFXR",
          staging: "price_1TABnNRsxD5c0WXpoWCNVWSm",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 150,
        productIds: {
          prod: null,
          staging: "prod_U8SigNiVkQR42r",
        },
        lookupKeys: {
          prod: null,
          staging: "standard_yearly_perseat_flat",
        },
        priceIds: {
          prod: "price_1SxVkQ2KQrGGmiVJfA2FC313",
          staging: "price_1TABnNRsxD5c0WXpSyM31oIu",
        },
      },
    },
    /* Machine-readable flags for Functions */
    entitlements: {
      features: {dam: false, storageGB: 25, maxAssets: 5000},
    },
  },

  /* ------------------------ PRO ------------------------- */
  pro: {
    kind: "base",

    /* ───────────────────────────────────────────────────────────────
     * Self-serve + CTA policy (NEW)
     * ---------------------------------------------------------------- */
    availability: {selfServe: true},
    cta: {
      type: "start_trial",
      label: "Start free trial",
    },

    /* Seat constraints
     *
     * UPDATED SEAT MODEL
     * ------------------
     * The new app pricing flow allows organizations to define their own
     * seat count directly in the UI instead of forcing the old
     * "starts at 10 seats / increments of 5" approach.
     *
     * Enterprise-safe rollout decision:
     * - minSeats: 1   → allows true typed seat selection from the new UI
     * - step: 1       → supports single-seat increments in the new UI
     * - maxSeats: 250 → keep existing self-serve upper bound for now
     *
     * IMPORTANT:
     * - We are intentionally changing the source-of-truth constraint layer
     *   here first so the UI and billing runtime can align to it.
     * - If you later want to expand or remove self-serve maximums, do that
     *   in a separate follow-up change after backend/runtime validation.
     */
    constraints: {
      minSeats: 1,
      maxSeats: 250,
      step: 1,
    },

    subheader: "Everything in standard, plus:",
    features: [
      {
        name: "Flexible user seats",
        tooltip: "Add from 1 user up to 250 users in self-serve.",
      },
      {
        name: "Unlimited form types",
        tooltip: "Create any number of custom forms for your projects.",
      },
      {
        name: "Unlocked Templates",
        tooltip:
          "App-wide or publish your own organization-wide form " +
          "templates.",
      },
      {
        name: "25 vanity form URLs",
        tooltip: "Customize up to 25 vanity URLs per plan.",
      },
      {name: "Asset-level UTM tracking", bold: true},
      {
        name: "Digital Asset Management-lite",
        tooltip:
          "Manage digital assets from one central place. Up to " +
          "5GB included.",
      },
      {
        name: "Digital Asset tagging",
        tooltip:
          "Tag digital assets with utm parameters for " +
          "calculating return on assets.",
      },
      {
        name: "Submissions per user",
        tooltip: "Track submissions per registered user.",
      },
      {
        name: "Form Submission Analytics",
        tooltip: "View form submissions from one place.",
      },
      {
        name: "Priority Email Support",
        tooltip: "Priority resposnes in less than 48 hours.",
      },
      {name: "Additional Integrations", bold: true},
      {
        name: "Google Looker Integration Add-on Available",
        tooltip: "Contact us for more details.",
      },
      {
        name: "Bitly Integration with click tracking",
        tooltip: "Bitly integration with click tracking.",
      },
      {name: "Custom Feature Dev Add-on Available."},
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 30,
        productIds: {
          prod: null,
          staging: "prod_U8SlCHHzlXPDf9",
        },
        lookupKeys: {
          prod: null,
          staging: "pro_monthly_perseat_flat",
        },
        priceIds: {
          prod: "price_1SxVkL2KQrGGmiVJkDoB83DY",
          staging: "price_1TABq0RsxD5c0WXpzBy9nbqB",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 300,
        productIds: {
          prod: null,
          staging: "prod_U8SlCHHzlXPDf9",
        },
        lookupKeys: {
          prod: null,
          staging: "pro_yearly_perseat_flat",
        },
        priceIds: {
          prod: "price_1SxVkL2KQrGGmiVJzU4Y716u",
          staging: "price_1TABq0RsxD5c0WXpdOqHuLOC",
        },
      },
    },
    entitlements: {
      features: {dam: true, storageGB: 100, maxAssets: 100000},
    },
  },

  /* --------------------- ENTERPRISE --------------------- */
  enterprise: {
    kind: "base",

    /* ───────────────────────────────────────────────────────────────
     * Self-serve + CTA policy (NEW)
     * ----------------------------------------------------------------
     * Enterprise is NOT self-serve.
     *
     * Best practice:
     *  • Sales (or an admin) provisions seats and contract terms.
     *  • UI should display a "Contact Sales" CTA and avoid numeric
     *    pricing.
     *  • Stripe flows should be blocked for Enterprise in Functions.
     * ---------------------------------------------------------------- */
    availability: {selfServe: false},
    cta: {
      type: "contact_sales",
      label: "Contact Sales",
      contact: {
        method: "url",
        url: "https://utmsmartmanager.com/demo-request",
      },
    },

    /* Pricing display policy (NEW; UI hint)
     *  - "contact" tells UI to render "Custom pricing" instead of $/seat.
     */
    pricingDisplay: "contact",

    /* Contract metadata (NEW; clarifies that seats are sales-entered) */
    contract: {
      salesLed: true,
      seatsEnteredBySales: true,
      billing: "invoice", // informational UI hint
      notes:
        "Enterprise is contract-based. Seats and pricing are " +
        "provisioned by Sales/admin; no self-serve checkout.",
    },

    /* Seat constraints
     *
     * UPDATED SEAT MODEL
     * ------------------
     * Even though Enterprise is sales-led / contact-sales, we still keep
     * the constraint contract aligned with the new seat-selection model.
     *
     * Enterprise-safe rollout decision:
     * - minSeats: 1   → keeps the source-of-truth seat model consistent
     * - step: 1       → aligns with single-seat selection semantics
     * - maxSeats: null → unlimited remains appropriate for enterprise
     *
     * IMPORTANT:
     * - Enterprise remains non-self-serve.
     * - These constraints are metadata and should not be interpreted as
     *   enabling self-serve checkout for Enterprise.
     */
    constraints: {
      minSeats: 1,
      maxSeats: null,
      step: 1,
    },

    subheader: "Includes pro, plus:",
    features: [
      {
        name: "Unlimited users",
        tooltip:
          "Enterprise seat counts are flexible and provisioned " +
          "through Sales.",
      },
      {
        name: "Unlimited Templates",
        tooltip: "Publish unlimited templates org-wide.",
      },
      {
        name: "50 vanity form URLs",
        tooltip: "Customize up to 25 vanity URLs per plan.",
      },
      {
        name: "Org-wide field management",
        tooltip: "Manage fields for the entire organization.",
      },
      {
        name: "Digital asset management tagging customizations",
        tooltip: "Customize tag management for digital assets.",
      },
      {
        name: "SSO Add on available",
        tooltip: "contact us about adding SSO.",
      },
      {name: "Invoice Billing"},
      {name: "Priority support with live Zoom support"},
      {name: "Custom Feature Dev Add-on Available"},
    ],

    /* ----------------------------------------------------------------
     * IMPORTANT:
     * Keep the pricing tiers object so:
     *  • UI code can safely read cfg.pricing[interval] without crashing
     *  • Enterprise can remain a first-class plan in the catalog
     *
     * NOTE:
     *  • Enterprise does not use a fixed Stripe priceId in self-serve.
     *  • Enterprise mapping should be derived from Stripe metadata
     *    (e.g., subscription.metadata.planKey = "enterprise") or an
     *    internal provisioning flow.
     *
     * Even though Enterprise is not self-serve, your back office may
     * later attach a real Stripe price to a customer (e.g. negotiated
     * price), and webhooks will still map priceId → "enterprise".
     *
     * UI should ignore numeric price when pricingDisplay === "contact".
     * ---------------------------------------------------------------- */
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: null,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: null,
          staging: null,
        },
      },
      annual: {
        flatFee: 0,
        perSeat: null,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: null,
          staging: null,
        },
      },
    },

    entitlements: {
      features: {dam: true, storageGB: 1000, maxAssets: 1000000},
    },
  },

  /* ----------------------- ADD-ONS ---------------------- */
  storage_pack_200gb: {
    kind: "addon",

    /* Add-ons are not self-serve in the UI today (kept hidden by kind). */
    availability: {selfServe: true},
    cta: {
      type: "addon",
      label: "Add",
    },

    subheader: "Storage add-on",
    features: [
      {
        name: "Extra 200 GB storage",
        tooltip: "Adds 200 GB to your org’s storage cap.",
      },
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_storage_200gb_month",
          staging: "price_addon_storage_200gb_month",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_storage_200gb_year",
          staging: "price_addon_storage_200gb_year",
        },
      },
    },
    entitlements: {features: {storageGB_add: 200}},
  },

  storage_pack_1tb: {
    kind: "addon",

    availability: {selfServe: true},
    cta: {
      type: "addon",
      label: "Add",
    },

    subheader: "Storage add-on",
    features: [
      {
        name: "Extra 1 TB storage",
        tooltip: "Adds 1 TB to your org’s storage cap.",
      },
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_storage_1tb_month",
          staging: "price_addon_storage_1tb_month",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_storage_1tb_year",
          staging: "price_addon_storage_1tb_year",
        },
      },
    },
    entitlements: {features: {storageGB_add: 1024}},
  },

  asset_pack_50k: {
    kind: "addon",

    availability: {selfServe: true},
    cta: {
      type: "addon",
      label: "Add",
    },

    subheader: "Asset-count add-on",
    features: [
      {
        name: "Extra 50k assets",
        tooltip: "Adds fifty thousand more assets to the cap.",
      },
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_assets_50k_month",
          staging: "price_addon_assets_50k_month",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_assets_50k_year",
          staging: "price_addon_assets_50k_year",
        },
      },
    },
    entitlements: {features: {maxAssets_add: 50000}},
  },

  asset_pack_200k: {
    kind: "addon",

    availability: {selfServe: true},
    cta: {
      type: "addon",
      label: "Add",
    },

    subheader: "Asset-count add-on",
    features: [
      {
        name: "Extra 200k assets",
        tooltip: "Adds two hundred thousand more assets.",
      },
    ],
    pricing: {
      monthly: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_assets_200k_month",
          staging: "price_addon_assets_200k_month",
        },
      },
      annual: {
        flatFee: 0,
        perSeat: 0,
        productIds: {
          prod: null,
          staging: null,
        },
        lookupKeys: {
          prod: null,
          staging: null,
        },
        priceIds: {
          prod: "price_addon_assets_200k_year",
          staging: "price_addon_assets_200k_year",
        },
      },
    },
    entitlements: {features: {maxAssets_add: 200000}},
  },
};

/* ──────────────────────────────────────────────────────────────
 * Helpers to resolve the active env view of the catalog
 * ─────────────────────────────────────────────────────────── */

function resolveValueByEnv(obj, env) {
  if (!obj || typeof obj !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(obj, env)) {
    return obj[env] || null;
  }
  return null;
}

function resolvePricingTierForEnv(tierConfig, env) {
  if (!tierConfig || typeof tierConfig !== "object") return tierConfig;

  return {
    ...tierConfig,
    productId: resolveValueByEnv(tierConfig.productIds, env),
    lookupKey: resolveValueByEnv(tierConfig.lookupKeys, env),
    priceId: resolveValueByEnv(tierConfig.priceIds, env),
  };
}

function resolvePlanConfigsForEnv(source, env) {
  return Object.entries(source).reduce((acc, [planKey, cfg]) => {
    const pricing = cfg.pricing || {};

    acc[planKey] = {
      ...cfg,
      pricing: Object.entries(pricing).reduce((pricingMap, [tierKey, tier]) => {
        pricingMap[tierKey] = resolvePricingTierForEnv(tier, env);
        return pricingMap;
      }, {}),
    };

    return acc;
  }, {});
}

/* Active, environment-resolved catalog (backwards-compatible export) */
const planConfigs = resolvePlanConfigsForEnv(
    rawPlanConfigs,
    ACTIVE_BILLING_ENV,
);

/* -------- priceId -> planKey (for Stripe lookups) ------- */
const priceIdToPlanKey = Object.entries(planConfigs).reduce(
    (map, [key, cfg]) => {
      const tiers = cfg.pricing || {};

      Object.values(tiers).forEach((tier) => {
        if (tier && tier.priceId) {
          map[tier.priceId] = key;
        }
      });

      return map;
    },
    {},
);

/* ---- planKey -> entitlements (server needs this map) ---- */
const planKeyToEntitlements = Object.entries(planConfigs).reduce(
    (map, [key, cfg]) => {
      const defaults = {
        features: {dam: false, storageGB: 0, maxAssets: 0},
      };

      const feats =
        (cfg.entitlements && cfg.entitlements.features) || {};

      map[key] = {
        plan: key,
        features: {...defaults.features, ...feats},
      };

      return map;
    },
    {},
);

/* --- priceId -> entitlements (handy in webhooks/sync) --- */
const priceIdToEntitlements = Object.entries(priceIdToPlanKey).reduce(
    (map, [pid, pkey]) => {
      map[pid] = planKeyToEntitlements[pkey];
      return map;
    },
    {},
);

/* ──────────────────────────────────────────────────────────────
 * Optional helper exports for debugging / explicit env access
 * ─────────────────────────────────────────────────────────── */

function getPlanConfigsForEnv(env) {
  return resolvePlanConfigsForEnv(rawPlanConfigs, normalizeBillingEnv(env));
}

function getPriceIdToPlanKeyForEnv(env) {
  const cfg = getPlanConfigsForEnv(env);

  return Object.entries(cfg).reduce((map, [key, plan]) => {
    const tiers = plan.pricing || {};

    Object.values(tiers).forEach((tier) => {
      if (tier && tier.priceId) {
        map[tier.priceId] = key;
      }
    });

    return map;
  }, {});
}

function getPriceIdToEntitlementsForEnv(env) {
  const envPriceIdToPlanKey = getPriceIdToPlanKeyForEnv(env);
  const envPlanConfigs = getPlanConfigsForEnv(env);

  const envPlanKeyToEntitlements = Object.entries(envPlanConfigs).reduce(
      (map, [key, cfg]) => {
        const defaults = {
          features: {dam: false, storageGB: 0, maxAssets: 0},
        };

        const feats =
          (cfg.entitlements && cfg.entitlements.features) || {};

        map[key] = {
          plan: key,
          features: {...defaults.features, ...feats},
        };

        return map;
      },
      {},
  );

  return Object.entries(envPriceIdToPlanKey).reduce((map, [pid, pkey]) => {
    map[pid] = envPlanKeyToEntitlements[pkey];
    return map;
  }, {});
}


 /* CommonJS export */
 module.exports = {
   ACTIVE_BILLING_ENV,
   BILLING_ENV_PROD,
   BILLING_ENV_STAGING,
   BILLING_POLICY,
   rawPlanConfigs,
   planConfigs,
   priceIdToPlanKey,
   planKeyToEntitlements,
   priceIdToEntitlements,
   normalizeBillingEnv,
   resolveBillingEnv,
   getPlanConfigsForEnv,
   getPriceIdToPlanKeyForEnv,
   getPriceIdToEntitlementsForEnv,

   /* Presentation/content contract exports */
   ...require("./pricingPresentationContract"),

   /* Presentation/content merge helper export */
   buildPricingPresentationPayload:
     require("./buildPricingPresentationPayload").buildPricingPresentationPayload,
 };